import { Tooltip } from "antd"
import { Form, FormikProvider, useFormik } from "formik"
import { forwardRef, PropsWithChildren, useEffect, useImperativeHandle, useState } from "react"
import TextField from "./fields/text-field"
import OutsideAlerter from "components/view-utils/outside-alerter"
import styled from "styled-components"


interface InPlaceEditContainerProps extends PropsWithChildren {
  isEditing?: boolean
  fieldComponent?: ReactNode
  tooltip?: string

  value: string
  onChange(value: string): void
}

export interface InPlaceEditContainerHandle {
  trigger(): void
}

const InPlaceEditContainer = forwardRef<InPlaceEditContainerHandle, InPlaceEditContainerProps>((props, ref) => {

  // Expore ref functionality
  useImperativeHandle(ref, () => ({
    trigger: () => {
      setIsEditing(true)
    }
  }))

  const formik = useFormik({
    initialValues: {
      value: props.value
    },
    enableReinitialize: true,
    onSubmit: ((values) => {
      props.onChange(values.value)
    })
  })

  const [isEditing, setIsEditing] = useState(!!props.isEditing)
  useEffect(() => { setIsEditing(!!props.isEditing)}, [props.isEditing])

  const cancelEditing = () => {
    setIsEditing(false)
    formik.setFieldValue('value', props.value)
    formik.resetForm()
  }

  const handleSubmit = () => {
    props.onChange(formik.values.value)
    setIsEditing(false)
    formik.resetForm()
  }

  const FieldComponent = props.fieldComponent || InPlaceTextField

  // Display form of the component
  if (isEditing) {
    return (
      <FormikProvider value={formik}>
        <Form 
          onSubmit={handleSubmit}
          onKeyDownCapture={(e) => {
            if (e.key == "Escape") {
              e.stopPropagation()
              cancelEditing()
            }
          }}
        >
          <OutsideAlerter
            onClick={(e) => {
              cancelEditing()
            }}
          >
            <FieldComponent />
          </OutsideAlerter>
        </Form>
      </FormikProvider>
    )
  }

  const previewContent = props.children || props.value
  return (
    <Tooltip title={props.tooltip}>
      <PreviewWrapper
        onClick={(e) => {
          setIsEditing(true)
        }}
        style={{
          width: '100%'
        }}
      >
        { previewContent }
      </PreviewWrapper>
    </Tooltip> 
  )
})

const PreviewWrapper = styled.span`
  cursor: pointer !important;
  padding: 5px;

  :hover {
    border-radius: 5px;
    background-color: #efefef;
  }
`


interface InPlaceTextFieldProps {
  onSubmit(): void
  width?: string
}

const InPlaceTextField = (props: InPlaceTextFieldProps) => (
  <TextField name="value" autoFocus={true} style={{ width: "100%" }}/>
)

export default InPlaceEditContainer